/**
 * Mark coordinate normalization (ra-or1y).
 *
 * The committee renders/looks-up marks as { id, latitude:number, longitude:number }
 * (CoursePreview + CourseCards use a strict `typeof === 'number'` guard). Marks
 * can arrive from several sources/shapes over the project's life:
 *   - canonical: { id, latitude:number, longitude:number }
 *   - app/newer schema: { mark_id, lat, lng }
 *   - form/string input: latitude/lat as "53.59" (string)
 *   - nested: { coordinates|location: { lat, lng } }
 * Any non-canonical shape makes the `typeof === 'number'` guard fail → the mark
 * drops out of the lookup → "No marks with coordinates" + (on an explicit save)
 * the `latitude: m.latitude || 0` write would zero it out (data loss).
 *
 * Normalizing every mark to { id, latitude:number, longitude:number } at the data
 * layer makes the committee robust to all of the above and removes the data-loss
 * vector. Marks whose coordinates can't be coerced to finite numbers are left as-is
 * (so the guard still excludes them — a genuinely coordinate-less mark).
 */

/**
 * Coerce a value to a finite number, or undefined. Accepts numbers and fully
 * numeric strings only — strict `Number()` (not `parseFloat`) so partially
 * numeric junk like "53.4abc" or "6.2W" is rejected rather than silently
 * placing a mark at a wrong/garbage position. Empty string → undefined
 * (Number('') is 0). (ra-or1y, codex review.)
 */
function toFiniteNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/** First candidate that coerces to a finite number, else undefined. Lets a valid
 *  fallback (e.g. `lat`) win when the canonical field is present-but-invalid
 *  (e.g. `latitude: ''`) on a mixed-shape mark. (ra-or1y, codex review.) */
function firstFinite(...candidates) {
  for (const c of candidates) {
    const n = toFiniteNumber(c);
    if (n !== undefined) return n;
  }
  return undefined;
}

/**
 * Normalize a single mark to canonical shape. Preserves all other fields.
 * @param {object} raw
 * @returns {object}
 */
export function normalizeMark(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  const nested = (raw.coordinates && typeof raw.coordinates === 'object' && raw.coordinates) ||
    (raw.location && typeof raw.location === 'object' && raw.location) || {};
  const latitude = firstFinite(raw.latitude, raw.lat, nested.latitude, nested.lat);
  const longitude = firstFinite(raw.longitude, raw.lng, nested.longitude, nested.lng);
  const id = raw.id ?? raw.mark_id ?? raw.markId;
  return {
    ...raw,
    ...(id != null ? { id } : {}),
    ...(latitude != null ? { latitude } : {}),
    ...(longitude != null ? { longitude } : {}),
  };
}

/**
 * Normalize an array of marks. Non-arrays → [].
 * @param {Array} marks
 * @returns {Array}
 */
export function normalizeMarks(marks) {
  return Array.isArray(marks) ? marks.map(normalizeMark) : [];
}
